import type { ReactNode } from 'react'
import { CaretRightIcon } from '@phosphor-icons/react'
import { Icon } from '@/components/ui/Icon'
import { cn } from '@/lib/cn'

type AppViewportProps = {
  children: ReactNode
}

export function AppViewport({ children }: AppViewportProps) {
  return <div className="app-viewport">{children}</div>
}

type AppContainerProps = {
  children: ReactNode
}

export function AppContainer({ children }: AppContainerProps) {
  return <div className="app-container">{children}</div>
}

type AppHeaderProps = {
  start?: ReactNode
  end?: ReactNode
  center?: ReactNode
  onBack?: () => void
}

export function AppHeader({ start, center, end, onBack }: AppHeaderProps) {
  const hasLeading = onBack != null || start != null
  const isTitle = typeof start === 'string'

  return (
    <header className={cn('app-header', { 'app-header--three-slots': center != null })}>
      {hasLeading ? (
        <div
          className={cn('app-header__start', {
            'app-header__start--fill': isTitle,
          })}
        >
          {onBack != null ? (
            <button
              type="button"
              className="app-header__icon-btn"
              aria-label="بازگشت"
              onClick={onBack}
            >
              <Icon as={CaretRightIcon} size="lg" />
            </button>
          ) : null}
          {isTitle ? (
            <div className="app-header__title">{start}</div>
          ) : (
            start
          )}
        </div>
      ) : null}
      {center != null ? <div className="app-header__center">{center}</div> : null}
      {end != null ? <div className="app-header__end">{end}</div> : null}
    </header>
  )
}

type AppMainProps = {
  children: ReactNode
  padded?: boolean
}

export function AppMain({ children, padded = true }: AppMainProps) {
  return (
    <main className={cn('app-main', { 'app-main--padded': padded })}>
      {children}
    </main>
  )
}

type AppFooterProps = {
  children: ReactNode
  bordered?: boolean
}

export function AppFooter({ children, bordered = false }: AppFooterProps) {
  return (
    <footer className={cn('app-footer', { 'app-footer--bordered': bordered })}>
      {children}
    </footer>
  )
}

type PageShellProps = {
  header?: ReactNode
  footer?: ReactNode
  children: ReactNode
  padded?: boolean
}

export function PageShell({
  header,
  footer,
  children,
  padded = true,
}: PageShellProps) {
  return (
    <div className="page-shell">
      {header}
      <AppMain padded={padded}>{children}</AppMain>
      {footer}
    </div>
  )
}
